import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ClipboardCheck, ArrowLeft, Shield, Database, Eye, Lock, Users, Mail } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center mb-4">
            <ClipboardCheck className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-foreground">QA Builder Pro</h1>
          <p className="text-muted-foreground mt-1">Privacy Policy</p>
        </div>

        {/* Back Button */}
        <div className="mb-6">
          <Link to="/register">
            <Button variant="ghost" size="sm" data-testid="back-to-register-btn">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Register
            </Button>
          </Link>
        </div>

        {/* Privacy Content */}
        <Card className="border-slate-200 shadow-lg">
          <CardHeader>
            <CardTitle className="font-heading text-2xl">Privacy Policy</CardTitle>
            <p className="text-sm text-muted-foreground">Last updated: December 2025</p>
          </CardHeader>
          <CardContent className="space-y-8">
            
            {/* Introduction */}
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">1. Introduction</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                <strong>QA Builder Pro</strong> ("Company", "we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our construction quality assurance application, QA Builder Pro.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                By using QA Builder Pro, you agree to the collection and use of information in accordance with this policy.
              </p>
            </section>

            {/* Information We Collect */}
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <Database className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">2. Information We Collect</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                We collect the following types of information:
              </p>
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div>
                  <span className="font-medium text-foreground">Account Information:</span>
                  <span className="text-muted-foreground ml-2">Name, email address, company name, and password (encrypted).</span>
                </div>
                <div>
                  <span className="font-medium text-foreground">QA Form Data:</span>
                  <span className="text-muted-foreground ml-2">Inspection checklists, photos, signatures, building details, and notes you create.</span>
                </div>
                <div>
                  <span className="font-medium text-foreground">Usage Data:</span>
                  <span className="text-muted-foreground ml-2">How you interact with the app, features used, and access times.</span>
                </div>
                <div>
                  <span className="font-medium text-foreground">Payment Information:</span>
                  <span className="text-muted-foreground ml-2">Processed securely by Paddle; we do not store your card details.</span>
                </div>
              </div>
            </section>

            {/* How We Use Your Information */}
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">3. How We Use Your Information</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Provide and maintain the QA Builder Pro service</li>
                <li>Process your QA forms and generate PDF reports</li>
                <li>Manage your account and subscription</li>
                <li>Send important service notifications</li>
                <li>Improve and develop new features</li>
                <li>Provide customer support</li>
                <li>Detect and prevent fraud or abuse</li>
              </ul>
            </section>

            {/* Data Security */}
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">4. Data Security</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                We implement industry-standard security measures to protect your data:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>All data is encrypted in transit using TLS/SSL</li>
                <li>Passwords are hashed using bcrypt encryption</li>
                <li>Database access is restricted and monitored</li>
                <li>Regular security audits and updates</li>
                <li>Payment processing handled by PCI-compliant Paddle</li>
              </ul>
            </section>

            {/* Data Sharing */}
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">5. Data Sharing</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                We do not sell your personal information. We may share your data only in these circumstances:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li><strong>Within your company:</strong> Team members in your company can see shared forms as per your role settings</li>
                <li><strong>Service providers:</strong> Paddle for payments, cloud hosting providers for infrastructure</li>
                <li><strong>Legal requirements:</strong> If required by law, court order, or government request</li>
                <li><strong>Business transfer:</strong> In connection with a merger or acquisition (with notice)</li>
              </ul>
            </section>

            {/* Your Rights */}
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">6. Your Rights</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                You have the right to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Access your personal data we hold</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your account and data</li>
                <li>Export your QA forms and data</li>
                <li>Object to certain processing activities</li>
                <li>Withdraw consent at any time</li>
              </ul>
            </section>

            {/* Data Retention */}
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <Database className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">7. Data Retention</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                We retain your data for as long as your account is active. Upon account deletion:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Personal data is deleted within 30 days</li>
                <li>Anonymized usage statistics may be retained</li>
                <li>Backup copies are purged within 90 days</li>
                <li>Legal/compliance records may be retained as required by law</li>
              </ul>
            </section>

            {/* Contact */}
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">8. Contact Us</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                If you have questions about this Privacy Policy or wish to exercise your rights, please contact us through the application or reach out to your company administrator.
              </p>
            </section>

            {/* Changes */}
            <section className="space-y-3">
              <h3 className="text-lg font-semibold text-foreground">9. Changes to This Policy</h3>
              <p className="text-muted-foreground leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date. Your continued use of QA Builder Pro after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            {/* Footer */}
            <div className="border-t pt-6 mt-8">
              <p className="text-sm text-muted-foreground text-center">
                By using QA Builder Pro, you acknowledge that you have read and understood this Privacy Policy.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer Links */}
        <div className="mt-8 text-center space-x-4">
          <Link to="/register" className="text-primary hover:underline text-sm font-medium">
            Create Account
          </Link>
          <span className="text-muted-foreground">|</span>
          <Link to="/login" className="text-primary hover:underline text-sm font-medium">
            Sign In
          </Link>
          <span className="text-muted-foreground">|</span>
          <Link to="/terms" className="text-primary hover:underline text-sm font-medium">
            Terms
          </Link>
          <span className="text-muted-foreground">|</span>
          <Link to="/refund" className="text-primary hover:underline text-sm font-medium">
            Refund Policy
          </Link>
        </div>
      </div>
    </div>
  );
}
